import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Client } from "assets/SideBarIcons/customer.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import {
  ClientProfileService,
  CommonProfileService,
} from "subModule/src/core/services";
import { DeleteData } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetCustomersService } from "subModule/src/services/client/useClient";
import {
  ClientResponse,
  ChangeStatusClientResponse,
} from "subModule/src/services/client/interface";
import CustomerSearchHeader from "components/Customer/CustomerSearchHeader";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
interface SearchFormData {
  [key: string]: string | number;
}
export default function CustomersListing() {
  const navigate = useNavigate();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusModal, setStatusModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["name"]);
  const [search, setSearch] = useState<any>({});
  const [row, setRow] = useState<ClientResponse>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    name: "",
    userName: "",
    phone: "",
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetCustomersService({ pageNumber: pageNo, size: pageSize, ...search });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => navigate("/customer/" + row.id);
  const showRowView = (row: any) => navigate("/customer/" + row.id);
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["name"]);
    setSearchFormData({ name: "", userName: "", phone: "" });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val =
      field === "type" ? (value ? parseInt(value.value) || 0 : 0) : value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    if (
      Object.keys(filteredSearch).length > 0 &&
      Object.values(filteredSearch).some((value) => value)
    ) {
      setSearch(filteredSearch);
      setPageNo(1);
      setPageSize(pageSize);
    }
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const DownloadCustomers = () => {
    CommonProfileService.exportEntityWithParams(
      { ...search },
      "customers/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Customers.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_CUSTOMER_UPDATE")
      ? checkPrivileges("ROLE_CUSTOMER_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_CUSTOMER_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/customer/create")}
        heading={t("customers")}
        icon={<Client height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_CUSTOMER_CREATE")}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            { id: "name", label: "name", numeric: false },
            { id: "userNameClient", label: "email", numeric: false },
            { id: "phone", label: "phone", numeric: false },
            { id: "address", label: "address", numeric: false },
            { id: "customerRefNo", label: "referenceNo", numeric: false },
            { id: "createdByName", label: "createdBy", numeric: false },
            ...(checkPrivileges("ROLE_CUSTOMER_ACTIVE")
              ? [{ id: "active", label: "status", numeric: false }]
              : []),
            ...(adjustActions(actionslist).length
              ? [{ id: "actions", label: "actions", numeric: false }]
              : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          onChangeStatus={onChangeStatus}
          rowClick={checkPrivileges("ROLE_CUSTOMER_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          headerComponent={
            <>
              <CustomerSearchHeader
                searchFormData={searchFormData}
                handleChangeFilter={handleChangeFilter}
                handleChangeSearch={handleChangeSearch}
                handleSubmitSearch={handleSubmitSearch}
                clearSearch={clearSearch}
                {...{ filter, search }}
              />
              {checkPrivileges("ROLE_CUSTOMER_EXPORT") ? (
                <Grid item className="text-end">
                  <Button
                    id="import-button"
                    variant="contained"
                    disableElevation
                    onClick={() => {
                      setDownloading(true);
                      DownloadCustomers();
                    }}
                    // endIcon={<KeyboardArrowDownIcon />}
                    className={`bg-white font-Saira font-[600] text-[13px] h-[31px] px-4 py-1 cursor-pointer rounded-[6px] ms-[10px]`}
                    sx={{
                      outline: "2px solid",
                      color: themeColors?.primary,
                      borderColor: themeColors?.primary,
                    }}
                    disabled={downloading}
                  >
                    {downloading ? (
                      <CircularProgress
                        sx={{ color: themeColors?.primary, marginRight: "8px" }}
                        size={16}
                      />
                    ) : (
                      ""
                    )}
                    {t("download")}
                  </Button>
                </Grid>
              ) : null}
            </>
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("Customer")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            ClientProfileService.deleteClient(
              (row as any).id,
              "customers/archive",
            )
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("Customer") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(<AlertMessages text={response.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            ClientProfileService.changeStatusClient(
              (row as any).id,
              row.active ? "inactive" : "active",
              { typeName: "customers" },
            )
              .then((response: ChangeStatusClientResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
